import { faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import React from 'react';
import Header from 'src/components/header.jsx';
import Loader from 'src/components/loader';
import MaintenanceFlow from 'src/components/maintenance/MaintenanceFlow';
import NotFound from 'src/components/not-found';
import userPropType from 'src/utils/user-prop-type';

/**
 * Shared scaffolding for the per-machine maintenance entry pages
 * (printer / post-processor / tool). Each page resolves its instance + type
 * through its own resource hooks and hands the query objects here; this
 * component owns the feature-gate / loading / not-found gates and the hand-off
 * to the anchor-agnostic MaintenanceFlow. The instance -> type link is read
 * generically from the anchor (`anchor.typeUriField`).
 */

const NotFoundScreen = ({ user, id }) => (
  <>
    <Header excludeFromHistory title='Not Found' user={user} />
    <main role='main' className='text-center'>
      <NotFound id={id} />
    </main>
  </>
);

NotFoundScreen.propTypes = {
  user: userPropType,
  id: PropTypes.string,
};

NotFoundScreen.defaultProps = {
  user: null,
  id: null,
};

const MaintenanceEntryPage = ({
  user,
  uuid,
  anchor,
  isEnabled,
  instanceQuery,
  typeQuery,
  initialEventUuid,
}) => {
  const instance = instanceQuery.data;
  const type = typeQuery.data || null;

  if (!isEnabled) {
    return <NotFoundScreen user={user} id={uuid} />;
  }

  if (instanceQuery.isInitialLoading || typeQuery.isInitialLoading) {
    return (
      <>
        <Header excludeFromHistory title='Maintenance' user={user} />
        <main role='main' className='text-center'>
          <Loader />
        </main>
      </>
    );
  }

  if (instanceQuery.isError || typeQuery.isError || !instance) {
    return <NotFoundScreen user={user} id={uuid} />;
  }

  return (
    <MaintenanceFlow
      anchor={anchor}
      instanceTargetUri={instance.uri}
      typeTargetUri={instance[anchor.typeUriField] || null}
      title={instance.name}
      subtitle={type?.name || null}
      icon={faScrewdriverWrench}
      user={user}
      initialEventUuid={initialEventUuid || null}
    />
  );
};

MaintenanceEntryPage.propTypes = {
  user: userPropType,
  uuid: PropTypes.string,
  anchor: PropTypes.object.isRequired,
  isEnabled: PropTypes.bool,
  instanceQuery: PropTypes.object.isRequired,
  typeQuery: PropTypes.object.isRequired,
  initialEventUuid: PropTypes.string,
};

MaintenanceEntryPage.defaultProps = {
  user: null,
  uuid: null,
  isEnabled: false,
  initialEventUuid: null,
};

export default MaintenanceEntryPage;
